/*
 * UICVerticalToolBar implements a small vertical toolbar UI component.
 *
 * The component is intentionally independent from Leaflet controls and can be
 * mounted into any DOM container. It renders action and toggle buttons with an
 * easy-button-like visual structure and delegates behavior through callbacks.
 */
export class UICVerticalToolBar {
    /**
     * Creates a small renderer-agnostic toolbar for buttons mounted over a 3D viewport.
     *
     * @param {{buttons?: object[]}} [options] Toolbar options.
     * @param {object[]} [options.buttons] Button descriptors for initial toolbar content.
     * @returns {UICVerticalToolBar} Initialized toolbar controller.
     */
    constructor(options = {}) {
        this.buttons = options.buttons || [];
        this.container = null;
        this.buttonElements = new Map();
    }

    /**
     * Mounts toolbar buttons into a DOM container.
     *
     * @param {HTMLElement} container Container that receives toolbar markup.
     * @returns {void}
     * @throws {Error} Throws when the container is missing.
     */
    mount(container) {
        if (!container) {
            throw new Error("UICVerticalToolBar container is required");
        }

        this.container = container;
        this.container.innerHTML = "";
        this.buttonElements.clear();
        this.buttons.forEach((button) => this._mountButton(button));
    }

    /**
     * Updates a mounted button state.
     *
     * @param {string} id Button identifier.
     * @param {{checked?: boolean, disabled?: boolean, title?: string, visible?: boolean}} state Partial button state.
     * @returns {void}
     */
    setButtonState(id, state) {
        const element = this.buttonElements.get(id);

        if (!element) {
            return;
        }

        if (state.checked !== undefined) {
            element.classList.toggle("selectedItem", Boolean(state.checked));
            element.dataset.checked = String(Boolean(state.checked));
        }
        if (state.disabled !== undefined) {
            element.classList.toggle("disabled", Boolean(state.disabled));
            element.setAttribute("aria-disabled", String(Boolean(state.disabled)));
        }
        if (state.title !== undefined) {
            element.title = state.title;
        }
        if (state.visible !== undefined) {
            element.hidden = !Boolean(state.visible);
        }
    }

    /**
     * Clears toolbar DOM references.
     *
     * @returns {void}
     */
    destroy() {
        if (this.container) {
            this.container.innerHTML = "";
        }

        this.container = null;
        this.buttonElements.clear();
    }

    /**
     * Creates one toolbar button and binds its click handler.
     *
     * @param {object} button Button descriptor.
     * @param {string} button.id Stable button identifier.
     * @param {string} [button.icon] Icon CSS class.
     * @param {string} [button.label] Fallback text label.
     * @param {string} [button.title] Button tooltip.
     * @param {boolean} [button.toggle=false] Whether the button toggles checked state.
     * @param {boolean} [button.checked=false] Initial checked state for toggle buttons.
     * @param {boolean} [button.disabled=false] Initial disabled state.
     * @param {boolean} [button.visible=true] Initial button visibility.
     * @param {Function} [button.onClick] Called after a non-toggle button click.
     * @param {Function} [button.onChange] Called as onChange(checked) after toggle state changes.
     * @returns {void}
     */
    _mountButton(button) {
        const element = document.createElement("a");
        element.className = "UICVerticalToolBar__button";
        element.href = "#";
        element.role = "button";
        element.title = button.title || button.label || button.id;
        element.dataset.buttonId = button.id;
        element.dataset.checked = String(Boolean(button.checked));
        element.hidden = button.visible === false;
        element.classList.toggle("disabled", Boolean(button.disabled));
        element.setAttribute("aria-disabled", String(Boolean(button.disabled)));
        element.classList.toggle("selectedItem", Boolean(button.toggle && button.checked));
        element.innerHTML = button.icon
            ? `<span class="button-state"><i class="${button.icon}" aria-hidden="true"></i></span>`
            : `<span class="button-state">${button.label || button.id}</span>`;

        element.addEventListener("click", (event) => {
            event.preventDefault();
            event.stopPropagation();

            if (element.classList.contains("disabled")) {
                return;
            }

            if (button.toggle) {
                const checked = element.dataset.checked !== "true";

                this.setButtonState(button.id, {checked});
                button.onChange?.(checked);
                return;
            }

            button.onClick?.();
        });

        this.buttonElements.set(button.id, element);
        this.container.appendChild(element);
    }
}
